import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useSkills } from "@/hooks/usePortfolio";
import { SectionTitle } from "@/components/SectionTitle";

const Counter = ({ to }) => {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  useEffect(() => {
    if (!inView) return;
    let frame; const start = performance.now(); const dur = 1400;
    const step = (t) => {
      const p = Math.min((t - start) / dur, 1);
      setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [inView, to]);
  return <span ref={ref}>{n}%</span>;
};

export const Skills = () => {
  const { data: skills, isLoading, error } = useSkills();

  if (isLoading || error || !skills) return null;

  const sortedSkills = [...skills].sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <section id="skills" className="section-pad relative" data-testid="skills-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/3 left-0 w-96 h-96 rounded-full" style={{ background: "radial-gradient(closest-side, hsl(var(--gold)/0.08), transparent)" }} />
      </div>
      <div className="container-x">
        <SectionTitle eyebrow="Skills" title={<>Mastered <span className="italic gold-text">competencies</span></>} subtitle="Proficiency built over two decades — measurable, demonstrable, and continually refined." />
        <div className="grid md:grid-cols-2 gap-x-12 gap-y-6">
          {sortedSkills.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              data-testid={`skill-${s.id}`}
            >
              <div className="flex items-end justify-between mb-2">
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">{s.skillCategory}</div>
                  <div className="font-serif text-lg mt-1">{s.skillName}</div>
                </div>
                <div className="font-serif text-2xl gold-text">
                  <Counter to={s.proficiencyPercentage} />
                </div>
              </div>
              <div className="progress-track">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: `${s.proficiencyPercentage}%` }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 1.4, ease: [0.2, 0.8, 0.2, 1], delay: 0.1 + i * 0.04 }}
                  className="progress-fill"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
